/**
 * Das Vorlese-Tempo — EIN Feld, EIN Raster. (29.09.2026, Ticket 2800)
 *
 * Martin, 29.09.2026: *„Das Vorlesetempo soll als Regler einstellbar sein,
 * mit vier Stufen und wenn möglich nach oben skalierbar (über 1,5-fach
 * hinaus)."* Die Stufen stehen im Blatt der Antwortlänge
 * (frontend/lib/schnell-einstellungen.ts), der feine Regler in den vollen
 * Einstellungen — beide schreiben `users.settings.speed`.
 *
 * ## Warum 2,0 geht
 *
 * Das Tempo wirkt am Server, nicht im Abspieler: Soniox- und ElevenLabs-
 * Stimmen dehnt /api/voice-preview per ffmpeg-atempo (server/loudness.ts,
 * mp3Tempo, 0,5–2,0), Google-Stimmen bekommen es als speakingRate. Soniox
 * selbst nimmt nur 0,7–1,3 an (server/soniox-tts.ts, SONIOX_TEMPO) und
 * bekommt es auf diesem Weg darum gar nicht. Am 29.09. an zwei echten
 * Soniox-Stücken über die echte Route und die echte Wiedergabe in Chromium
 * gemessen: bis 2,0 bleiben Tonhöhe, Höhen und Pegel wie bei 1,0, und jede
 * Datei läuft ohne Aussetzer genau Länge/Tempo lang.
 *
 * ## Warum ein Raster
 *
 * Der Regler hat Schritte von 0,1; ein Wert dazwischen stünde anders da,
 * als er klingt. Die Stufen aus Ticket 2736 (0,85 / 1,15) und alte
 * Regler-Werte (Schritt 0,05) rücken darum beim Lesen auf den nächsten
 * Schritt, genau in der Mitte nach oben: 1,15 → 1,2 („Zügig“), 0,85 → 0,9.
 * Ein Umzug im Speicher ist nicht nötig — Gerät (settings.ts, loadSettings)
 * und Server (settings-sanitize.ts) lesen durch dieselbe Funktion.
 *
 * ⚠️ Nicht für den Anruf: agent/index.ts und die Anruf-Metadaten
 *    (server/index.ts) behalten 0,5–1,5, denn im Erzähl-Modus geht das Tempo
 *    an ElevenLabs (`speed`, laut agent/pipeline.ts 0,7–1,2). Die Einstellungen
 *    sagen es seit dem 05.07.: der Regler gilt fürs Vorlesen, nicht fürs
 *    Telefonat.
 */
export const VORLESE_TEMPO = { min: 0.7, max: 2, schritt: 0.1 } as const;

/**
 * Was bisher als Tempo galt, beginnt hier — darunter ist es kein Tempo.
 * Werte zwischen 0,5 und 0,7 (ältere Regler) werden zu 0,7 statt zu 1,0.
 */
const UNTERGRENZE_ALT = 0.5;

/** Das Tempo aus einem gespeicherten Wert, im Raster — `null`, wenn es keines ist. */
export function vorleseTempo(roh: unknown): number | null {
  if (typeof roh !== "number" || !Number.isFinite(roh)) return null;
  if (roh < UNTERGRENZE_ALT || roh > VORLESE_TEMPO.max) return null;
  const jeEins = Math.round(1 / VORLESE_TEMPO.schritt);
  // Der kleine Zuschlag hält „genau in der Mitte“ fest: ein Rechenrest wie
  // 1,1499999999 soll wie 1,15 nach oben gehen, nicht nach unten.
  const geraster = Math.round(roh * jeEins + 1e-9) / jeEins;
  return Math.min(VORLESE_TEMPO.max, Math.max(VORLESE_TEMPO.min, geraster));
}
